/**
 * A booking's TIME and LENGTH as text — the one place every list and card asks.
 *
 * C16 (22/10/2026, Rob chose the mock-up's option A). A timed booking (bookable
 * granularity, built 15/07/2026) showed its times on one screen only — the employee leave
 * page, with its own copy of the `padStart` code — and everywhere else read as a plain day
 * costing "0.27 days". Now My Leave, the dashboard, the approver's card and the employee
 * leave page all write it the same way, from here.
 *
 * The rules (`bookingTime.checks.mjs` pins each one):
 *
 * 1. TIMES ARE 24-HOUR HH:MM, WRITTEN "10:00-12:00" — the same as the approver's email,
 *    the Teams and Slack cards and the calendar feed (backend `services/ical_feed.py`),
 *    so the screen and the message about the same booking never disagree.
 * 2. A TIMED BOOKING'S LENGTH IS IN HOURS FOR EVERYONE ("2h") — the same as the booking
 *    box shows when it is made. "0.27 days" means nothing to anybody.
 * 3. A WHOLE OR HALF DAY IS IN THE OWNER'S UNIT (A3): days people see "1 day" / "0.5 days",
 *    hours people see "7h 30m" — from the stamped minutes, never days × a day length
 *    (`leaveUnits.js` rule 1). No minutes on the row => days, never "0h".
 */
import { formatLeave, formatMinutes, normUnit, HOURS } from './leaveUnits.js';
import { nextWorkingDay } from './workingDays.js';

const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/** 600 -> "10:00", 1439 -> "23:59". Anything that isn't a minute of the day -> "". */
export function clockTime(minute) {
  if (!isNum(minute)) return '';
  const m = Math.round(Number(minute));
  if (m < 0 || m > 24 * 60) return '';
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** True when the booking is a timed one (both ends set). */
export function isTimed(booking) {
  return !!booking && isNum(booking.start_minute) && isNum(booking.end_minute);
}

/** "10:00-12:00" for a timed booking, "" for anything else. */
export function timeWindow(booking) {
  if (!isTimed(booking)) return '';
  return `${clockTime(booking.start_minute)}-${clockTime(booking.end_minute)}`;
}

/**
 * The booking's length: "2h" for a timed booking, otherwise in `unit` (default: the
 * owner's unit the API sent as `display_unit`).
 */
export function bookingLength(booking, unit) {
  if (!booking) return '';
  const u = normUnit(unit !== undefined ? unit : booking.display_unit);
  if (isTimed(booking)) {
    const mins = isNum(booking.total_minutes)
      ? Number(booking.total_minutes)
      : Number(booking.end_minute) - Number(booking.start_minute);
    return formatMinutes(mins);
  }
  return formatLeave(booking.total_days, {
    unit: u === HOURS ? HOURS : 'days',
    minutes: isNum(booking.total_minutes) ? Number(booking.total_minutes) : null,
  });
}

/**
 * When they are back at work: `{ date, time }`.
 *
 * ⚠️ A TIMED booking is back the SAME day, at its end time ("Thu 29 Oct, from 12:00").
 * Everything else is back on THEIR next working day after the last day (not the next
 * weekday — somebody in Algiers whose leave ends on a Thursday is back on Sunday). Before
 * C16 a 2-hour appointment said "back at work tomorrow" on My Leave, the wallchart and
 * Who's off.
 */
export function backAtWork(booking, workingDays) {
  if (!booking || !booking.end_date) return null;
  const [y, m, d] = String(booking.end_date).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  if (isTimed(booking)) return { date: new Date(y, m - 1, d), time: clockTime(booking.end_minute) };
  return { date: nextWorkingDay(new Date(y, m - 1, d + 1), workingDays), time: '' };
}

/**
 * How much of their day a TIMED booking takes, 0.15–0.9, for drawing it in a day cell — or
 * null when it isn't a timed booking (Rob chose C, 23/10/2026: a band from the bottom, as
 * tall as the share of their day, with a clock).
 *
 * A timed booking's `total_days` IS that share — it is priced as its minutes ÷ that
 * person's average day (services/leave_units.booking_days) — so no day length has to be
 * sent. Without it, the window over a 7h 30m day. Clamped so 15 minutes still shows and a
 * 7-hour booking never looks like a full day (which is drawn differently).
 */
export const FALLBACK_DAY_MINUTES = 450;
export function dayShare(booking) {
  if (!isTimed(booking)) return null;
  let share;
  if (isNum(booking.total_days) && Number(booking.total_days) > 0) {
    share = Number(booking.total_days);
  } else {
    const mins = isNum(booking.total_minutes)
      ? Number(booking.total_minutes)
      : Number(booking.end_minute) - Number(booking.start_minute);
    share = mins / FALLBACK_DAY_MINUTES;
  }
  return Math.min(0.9, Math.max(0.15, share));
}

/** `date` + ", 10:00-12:00" when timed — for lines that already print the date their way. */
export function withTimes(dateText, booking) {
  const w = timeWindow(booking);
  return w ? `${dateText}, ${w}` : dateText;
}
